import React from "react"
import { Col, Progress, Row, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHouse } from "@fortawesome/pro-regular-svg-icons"
import { IClub } from "./utils"

export default function MeetClubStatus({
  club,
  meetstatus: meetStatus,
  isHome,
  overallMeetStatus,
  entriesCount,
  confirmLineup,
  heatSheetAccepted,
}: {
  club: IClub
  meetstatus: 0 | 2 | 1 | null | undefined
  isHome: boolean
  overallMeetStatus?: 0 | 2 | 1 | null | undefined
  entriesCount?: number
  confirmLineup?: boolean
  heatSheetAccepted?: boolean
}): JSX.Element {
  const hasDetailedTrackerData =
    overallMeetStatus !== undefined &&
    entriesCount !== undefined &&
    confirmLineup !== undefined &&
    heatSheetAccepted !== undefined

  if (!hasDetailedTrackerData) {
    return (
      <Row align="middle" gutter={8} style={{ marginBottom: 4 }}>
        <Col
          style={{
            width: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isHome ? <FontAwesomeIcon icon={faHouse} style={{ verticalAlign: "center" }} /> : null}
        </Col>
        <Col
          flex="auto"
          style={{
            display: "flex",
            alignItems: "center",
            minHeight: 20,
          }}
        >
          <Tooltip title={club.nameLong || club.name} placement="topLeft">
            <span
              style={{
                display: "inline-block",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
              }}
            >
              {club.nameLong || club.name}
            </span>
          </Tooltip>
        </Col>
        <Col style={{ width: 110 }}>
          <Tooltip title="Entries started">
            <Progress
              style={{ width: "33%" }}
              showInfo={false}
              percent={meetStatus !== null ? 100 : 0}
              strokeColor="#1890ff"
              railColor="#bdbdbd"
            />
          </Tooltip>
          <Tooltip title="Heat Sheet Merged">
            <Progress
              style={{ width: "33%" }}
              showInfo={false}
              percent={meetStatus === 1 || meetStatus === 2 ? 100 : 0}
              strokeColor="#1890ff"
              railColor="#bdbdbd"
            />
          </Tooltip>
          <Tooltip title="Results Published">
            <Progress
              style={{ width: "33%" }}
              showInfo={false}
              percent={meetStatus === 2 ? 100 : 0}
              strokeColor="#1890ff"
              railColor="#bdbdbd"
            />
          </Tooltip>
        </Col>
      </Row>
    )
  }

  if (overallMeetStatus === 1 || overallMeetStatus === 2) {
    const latePhaseMeta =
      overallMeetStatus === 2
        ? {
            label: "Results Published",
            background: "#f6ffed",
            color: "#389e0d",
            border: "1px solid #b7eb8f",
          }
        : {
            label: "Heat Sheet Ready",
            background: "#e6f4ff",
            color: "#0958d9",
            border: "1px solid #91caff",
          }

    return (
      <Row align="middle" gutter={8} style={{ marginBottom: 8, minHeight: 30 }}>
        <Col
          style={{
            width: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isHome ? <FontAwesomeIcon icon={faHouse} style={{ verticalAlign: "center" }} /> : null}
        </Col>
        <Col
          flex="auto"
          style={{
            display: "flex",
            alignItems: "center",
            minHeight: 20,
          }}
        >
          <Tooltip title={club.nameLong || club.name} placement="topLeft">
            <span
              style={{
                display: "inline-block",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
              }}
            >
              {club.nameLong || club.name}
            </span>
          </Tooltip>
        </Col>
        <Col>
          <span
            style={{
              padding: "3px 9px",
              borderRadius: 999,
              background: latePhaseMeta.background,
              color: latePhaseMeta.color,
              border: latePhaseMeta.border,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {latePhaseMeta.label}
          </span>
        </Col>
      </Row>
    )
  }

  const lineupCompleted = Boolean(confirmLineup)
  const heatSheetReady = Boolean(heatSheetAccepted)
  const entriesStarted =
    (entriesCount || 0) > 0 || lineupCompleted || heatSheetReady

  const steps = [
    { label: "Entries Started", done: entriesStarted },
    { label: "Entries Completed", done: lineupCompleted },
    { label: "Heat Sheet Ready", done: heatSheetReady },
  ] as const

  const firstIncompleteIndex = steps.findIndex((step) => !step.done)
  const activeIndex = firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex

  return (
    <Row align="middle" gutter={8} style={{ marginBottom: 6, minHeight: 30 }}>
      <Col
        style={{
          width: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isHome ? <FontAwesomeIcon icon={faHouse} style={{ verticalAlign: "center" }} /> : null}
      </Col>
      <Col
        flex="auto"
        style={{
          display: "flex",
          alignItems: "center",
          minHeight: 20,
        }}
      >
        <Tooltip title={club.nameLong || club.name} placement="topLeft">
          <span
            style={{
              display: "inline-block",
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.2,
            }}
          >
            {club.nameLong || club.name}
          </span>
        </Tooltip>
      </Col>
      <Col style={{ width: 320 }}>
        <div
          style={{
            display: "flex",
            width: "100%",
            gap: 4,
          }}
        >
          {steps.map((step, idx) => {
            const isDone = step.done
            const isActive = !isDone && idx === activeIndex

            let background = "#f5f5f5"
            let color = "#595959"
            let border = "1px dashed #d9d9d9"
            let fontWeight: 500 | 600 = 500

            if (isDone) {
              background = "#52c41a"
              color = "#ffffff"
              border = "1px solid #52c41a"
              fontWeight = 600
            } else if (isActive) {
              background = "#e6f4ff"
              color = "#0958d9"
              border = "2px solid #1677ff"
              fontWeight = 600
            }

            return (
              <Tooltip key={`${club.clubId}_${step.label}`} title={step.label}>
                <div
                  style={{
                    flex: 1,
                    borderRadius: 999,
                    padding: "2px 6px",
                    fontSize: 11,
                    lineHeight: 1.3,
                    textAlign: "center",
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    background,
                    color,
                    border,
                    fontWeight,
                  }}
                >
                  {isDone ? "✓ " : `${idx + 1}. `}
                  {step.label}
                </div>
              </Tooltip>
            )
          })}
        </div>
      </Col>
      <Col
        style={{
          width: 80,
          textAlign: "right",
          fontSize: 11,
        }}
      >
        {entriesStarted && lineupCompleted && heatSheetReady ? (
          <span
            style={{
              padding: "1px 6px",
              borderRadius: 999,
              background: "#f6ffed",
              color: "#389e0d",
              border: "1px solid #b7eb8f",
              fontWeight: 600,
            }}
          >
            Ready
          </span>
        ) : (
          <span style={{ color: "#8c8c8c" }}>
            {`${[entriesStarted, lineupCompleted, heatSheetReady].filter(Boolean).length} of 3`}
          </span>
        )}
      </Col>
    </Row>
  )
}